import { defineStore } from 'pinia'
import { ObjWithPrivateProps } from '@/utils/classes'
import { getKey, makeBulkDeleteAction, makeClearErrorsAction, makeFetchAvailableAction, makeSetErrorsAction, makeStartFetchAction, makeStopFetchAction } from '@/utils/helpers/pinia'
import i18n from '@/utils/plugins/i18n'
import { AVAILABLE, cycloidDefault, FILTERS } from './_constants'
import { useAlertsStore } from '../alerts'
import { useLicenceStore } from './licence'
import { useNavConfigStore } from './navConfig'
import { useOidcMappingsStore } from './oidcMappings'
import { useAuthStore } from '../auth'
import { useCustomersStore } from '../customers'
import { useProjectStore } from './project'
import { useRootStore } from '../root'

/** Aligned with API naming_rules schema (regex max 255, message max 500). */
export const EMPTY_ORG_NAMING_RULES = {
  project_regex: '',
  project_message: '',
  env_regex: '',
  env_message: '',
  component_regex: '',
  component_message: '',
}

export const state = () => ({
  detail: null,
  environment: null,
  available: {
    ...AVAILABLE,
    tags: {},
  },
  errors: {
    ...AVAILABLE,
    organization: [],
    tags: [],
    environment: [],
  },
  fetchInProgress: {
    ..._.mapValues(AVAILABLE, () => false),
    organization: false,
    summary: false,
    environment: false,
  },
  KPIs: [],
  filters: FILTERS,
  cycloidCredentials: _(cycloidDefault.credentials).map((key) => [key, null]).fromPairs().value(),
  projectsPagination: null,
  environmentComponentsPagination: null,
  workersFetched: false,
  orgCanonicalToSwitchAfterDelete: null,
  namingRules: { ...EMPTY_ORG_NAMING_RULES },
  // Bumped whenever naming rules are created/updated/deleted, so unrelated
  // components (e.g. the naming-rules activity feed) can react to the mutation
  // itself, not just to the resulting data.
  namingRulesUpdatedAt: 0,
  summary: {
    catalogRepositories: 0, // service_catalog_sources
    configRepositories: 0,
    credentials: 0,
    members: 0, // users
    pipelines: 0,
    projects: 0,
    roles: 0,
    stacks: 0, // service_catalogs
    teams: 0,
  },
})

function applySpecificProps (state, propName, items) {
  return _.isArray(items)
    ? {
        credentials: items.map((credential) => new ObjWithPrivateProps(credential, {
          _isDefault: cycloidDefault.credentials.includes(credential.canonical),
        })),
        projects: items.map((project) => new ObjWithPrivateProps(project, {
          _cloudProviders: _(project.environments)
            .map('cloud_provider')
            .filter()
            .uniqBy('canonical')
            .value(),
        })),
        roles: items.map((role) => new ObjWithPrivateProps(role, {
          _isDefault: role.default ?? false,
        })),
        stacks: items.map((stack) => new ObjWithPrivateProps(stack, {
          _catalogRepositoryName: _.get(state.available.catalogRepositories.find(({ canonical }) => canonical === stack.service_catalog_source_canonical), 'name', null),
        })),
      }[propName] || items
    : items
}

/** @internal */
export const _privateFunctions = {
  applySpecificProps,
}

export const useOrganizationStore = defineStore('organization', {
  state,

  getters: {
    getAvailableByCanonical: (state) => (resource, canonical) => _.find(state.available[resource], ['canonical', canonical]),
    getAvailableById: (state) => (resource, id) => _.find(state.available[resource], ['id', id]),
    getCredentialsByType: (state) => (credentialType) => state.available.credentials.filter(({ type }) => credentialType === type),
    hasCredentialsOfType: (state) => (credentialType) => state.available.credentials.some(({ type }) => credentialType === type),
    hasCycloidCredentialOfType: (state) => (canonical) => !_.isEmpty(state.cycloidCredentials?.[canonical]),
    hasRunningWorkers: (state) => !state.workersFetched || _.some(state.available.workers, ['state', 'running']),
    hasAvailable: (state) => (key) => {
      if (getKey(key) !== key) {
        console.warn(`[hasAvailable]: please change to use the preferred key, as used on the state`, { keyPassed: key, keyPreferred: getKey(key) })
      }
      return !_.$isEmpty(state.available[key])
    },
    getErrorsByKey: (state) => (key) => {
      if (getKey(key) !== key) {
        console.warn(`[getErrorsByKey]: please change to use the preferred key, as used on the state`, { keyPassed: key, keyPreferred: getKey(key) })
      }
      return state.errors?.[getKey(key)] ?? []
    },
    subscriptionMembersLeftPercentage: ({ detail }) => {
      const { current_members: membersCount = null, members_count: maxMembers = null } = detail?.subscription || {}
      return _.some([membersCount, maxMembers], _.isNull)
        ? null
        : Math.round((maxMembers - membersCount) * 100 / maxMembers)
    },
    isOrgSelected: (state) => _.has(state.detail, 'canonical'),
    orgMembers: (state) => {
      const authStore = useAuthStore()
      return _
        .chain(state.available.members)
        .filter(['invitation_state', 'accepted'])
        .sortBy([
          (member) => !_.isEqual(member.username, authStore.username),
          (member) => member.full_name?.toLowerCase(),
          'username',
        ])
        .value()
    },
    organization: (state) => state.detail,
    orgCanonical: (state) => _.get(state.detail, 'canonical', ''),
    orgName: (state) => _.get(state.detail, 'name', ''),
    projectCanonical: () => {
      const projectStore = useProjectStore()
      return projectStore.projectCanonical
    },
    hasErrors: (state) => _.fromPairs(Object.entries(state.errors).map(([key, value]) => [key, _.isEmpty(value)])),
    favoriteProjects: (state) => _.filter(state.available.projects, { favorite: true }),
  },

  actions: {
    /**
     * @param {{ key: string, value: any }} available
     */
    SET_AVAILABLE ({ key, value }) {
      const available = applySpecificProps(this, key, value)

      if (key === 'workers') this.workersFetched = true

      const sortedAvailable = {
        appearances: _.sortBy(available, ({ canonical }) => canonical !== 'default'),
      }?.[key] ?? available

      this.available[key] = sortedAvailable
    },
    CLEAR_ERRORS: makeClearErrorsAction(state),
    SET_ERRORS: makeSetErrorsAction(),
    /** @param {string} [keyPath] Optional key to reset specific state */
    RESET_ORG_STATE (keyPath) {
      const initialState = state()
      if (!!keyPath && !_.has(initialState, keyPath)) return console.error(`[RESET_ORG_STATE] was trying to reset a keyPath that does not exist: 'organization.state.${keyPath}'`)
      if (keyPath) _.set(this, keyPath, _.cloneDeep(_.get(initialState, keyPath)))
      else {
        for (const key in initialState) {
          this[key] = _.cloneDeep(initialState)[key]
        }
      }
    },
    async FETCH_APP_APPEARANCE (orgCanonical = null) {
      const rootStore = useRootStore()
      return rootStore.FETCH_APP_APPEARANCE(orgCanonical)
    },
    FETCH_AVAILABLE: makeFetchAvailableAction(),
    BULK_DELETE: makeBulkDeleteAction(),

    async GET_PROJECTS ({ pagination = {}, filters = {}, orderBy } = {}) {
      this.START_FETCH('projects')
      const resolvedPagination = { page_index: 1, page_size: 20, ...pagination }
      const response = await this.$cycloid.ydAPI.getProjects(
        this.orgCanonical,
        { pagination: resolvedPagination, filters, orderBy },
      )
      this.RECEIVE_PROJECTS(response || {})
    },
    // Applies a /projects response to the list state. Pair with START_FETCH('projects').
    RECEIVE_PROJECTS ({ data, errors, pagination } = {}) {
      if (data) {
        this.SET_AVAILABLE({ key: 'projects', value: data })
        this.projectsPagination = pagination ?? null
      }
      if (errors) useAlertsStore().SHOW_ALERT({ type: 'error', content: errors })
      this.STOP_FETCH('projects')
    },

    async GET_ENVIRONMENT_COMPONENTS ({ projectCanonical, envCanonical, pagination = {} } = {}) {
      const alertStore = useAlertsStore()
      this.START_FETCH('environmentComponents')
      const resolvedPagination = { page_index: 1, page_size: 20, ...pagination }
      const { data, errors, pagination: paginationData } = (await this.$cycloid.ydAPI.getEnvironmentComponents(
        this.orgCanonical,
        projectCanonical,
        envCanonical,
        resolvedPagination,
      )) || {}
      if (data) {
        this.SET_AVAILABLE({ key: 'environmentComponents', value: data })
        this.environmentComponentsPagination = paginationData ?? null
      }
      if (errors) {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
      }
      this.STOP_FETCH('environmentComponents')
    },

    async GET_CYCLOID_CREDENTIALS () {
      const successData = []
      const { orgCanonical } = this

      const promises = cycloidDefault.credentials.map(async (credentialCanonical) => {
        const { data } = (await this.$cycloid.ydAPI.getCycloidCredential(orgCanonical, credentialCanonical)) || {}
        if (data) successData.push(data)
      })
      await Promise.allSettled(promises)

      for (const data of successData) {
        this.SET_CYCLOID_CREDENTIAL(data)
      }
    },
    async GET_ORGANIZATION (orgCanonical) {
      if (_.isEmpty(orgCanonical)) return console.error('[GET_ORGANIZATION] was not passed the required orgCanonical param')

      this.CLEAR_ERRORS('organization')
      const { data, errors } = (await this.$cycloid.ydAPI.getOrg(orgCanonical)) || {}
      if (data) {
        this.SET_ORGANIZATION(data)
        await this.FETCH_APP_APPEARANCE(orgCanonical)
      }
      if (errors) this.SET_ERRORS({ key: 'organization', errors })
    },
    async GET_ORG_ENVIRONMENT (envCanonical) {
      if (_.isEmpty(envCanonical)) return console.error('[GET_ORG_ENVIRONMENT] was not passed the required envCanonical param')

      this.START_FETCH('environment')
      this.CLEAR_ERRORS('environment')
      const { data, errors } = await this.$cycloid.ydAPI.getOrganizationEnvironment(this.orgCanonical, envCanonical) || {}
      if (data) {
        const variables = (data.variables || []).map((variable) => ({ ...variable }))
        this.SET_ENVIRONMENT({ ...data, variables })
      }
      if (errors) this.SET_ERRORS({ key: 'environment', errors })
      this.STOP_FETCH('environment')
    },
    SET_ENVIRONMENT (environment) {
      this.environment = environment
    },
    async UPDATE_ORG_ENVIRONMENT ({ environment }) {
      const alertsStore = useAlertsStore()

      this.CLEAR_ERRORS('environment')
      const variables = (environment.variables || []).map(({ updated_at, updated_by, created_at, ...rest }) => rest)
      const cloudAccountCanonicals = Array.isArray(environment.cloud_account_canonicals)
        ? environment.cloud_account_canonicals
        : (environment.cloud_accounts || []).map(({ canonical }) => canonical)
      const { cloud_accounts, cloud_account_canonicals, ...environmentBody } = environment
      const response = await this.$cycloid.ydAPI.updateOrganizationEnvironment(this.orgCanonical, {
        ...environmentBody,
        variables,
        cloud_account_canonicals: cloudAccountCanonicals,
        type: environment.type || environment.environment_type?.canonical,
      })
      const { data, errors } = response || {}

      if (errors || !response) {
        if (errors) {
          alertsStore.SHOW_ALERT({ type: 'error', content: errors })
          this.SET_ERRORS({ key: 'environment', errors })
        }
        return false
      }

      this.SET_ENVIRONMENT(data || environment)
      const content = i18n.global.t('alerts.success.environment.updated')
      alertsStore.SHOW_ALERT({ type: 'success', content })
      return true
    },
    async DELETE_ORG_ENVIRONMENT ({ envCanonical }) {
      const alertsStore = useAlertsStore()

      this.CLEAR_ERRORS('environment')
      const { errors } = await this.$cycloid.ydAPI.deleteOrganizationEnvironment(this.orgCanonical, envCanonical) || {}

      if (errors) {
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_ERRORS({ key: 'environment', errors })
      } else {
        this.environment = null
        const content = i18n.global.t('alerts.success.environment.deleted')
        alertsStore.SHOW_ALERT({ type: 'success', content })
      }
      return _.isEmpty(errors)
    },
    SET_CYCLOID_CREDENTIAL (credential) {
      if (cycloidDefault.credentials.includes(credential.canonical)) this.cycloidCredentials[credential.canonical] = credential
    },
    SWITCH_TO_OTHER_ORG_AFTER_DELETE (nextOrganization) {
      this.orgCanonicalToSwitchAfterDelete = nextOrganization
    },
    RESET_SWITCH_TO_OTHER_ORG_AFTER_DELETE () {
      this.orgCanonicalToSwitchAfterDelete = null
    },
    RESTORE_ORGANIZATION () {
      if (this.detail) throw new Error('[RESTORE_ORGANIZATION]: Organization MUST NEVER be restored from the local storage when the store contains an organization')

      const organization = sessionStorage.getItem(LSK.ORGANIZATION) || localStorage.getItem(LSK.ORGANIZATION)
      if (organization) this.detail = JSON.parse(organization)
    },
    async CREATE_ORGANIZATION ({ reqPage, $router, organization, parentCanonical = null }) {
      const rootStore = useRootStore()
      const alertsStore = useAlertsStore()

      const { data, errors = null } = parentCanonical
        ? (await this.$cycloid.ydAPI.createOrgChild(organization, parentCanonical)) || {}
        : (await this.$cycloid.ydAPI.createOrg(organization)) || {}
      if (data) {
        const { canonical: orgCanonical, name: orgName } = data
        await rootStore.FETCH_ORGS({ reqPage })
        await this.CHANGE_ORGANIZATION({ nextCanonical: orgCanonical })
        $router.push({ name: 'orgSettings', params: { orgCanonical } })
        alertsStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.organization.created', { orgName }) })
      }
      if (errors) alertsStore.SHOW_ALERT({ type: 'error', content: errors })
    },
    async CHANGE_ORGANIZATION (args) {
      const authStore = useAuthStore()
      const switchPromise = this._performOrgSwitch(args)
      authStore.SET_ORG_SWITCH_IN_FLIGHT(switchPromise)
      try {
        return await switchPromise
      } finally {
        if (authStore._orgSwitchInFlight === switchPromise) authStore.SET_ORG_SWITCH_IN_FLIGHT(null)
      }
    },
    async _performOrgSwitch ({ nextCanonical, parentCanonical }) {
      const authStore = useAuthStore()
      const rootStore = useRootStore()
      const customersStore = useCustomersStore()

      const refreshTokenError = await authStore.REFRESH_TOKEN({ query: parentCanonical
        ? { organization_canonical: parentCanonical, child_canonical: nextCanonical }
        : { organization_canonical: nextCanonical } })

      if (refreshTokenError === 'MFAError') return false
      const token = authStore.jwt
      if (!token) return false

      // Kick the nav config in parallel with /user + /org (ENG-361). Fire-and-forget on purpose:
      // awaiting a fetch inside the switch that 403s would call REFRESH_TOKEN(), which returns
      // this in-flight switch promise — a self-deadlock. FETCH_NAV_CONFIG dedupes its own retry.
      useNavConfigStore().FETCH_NAV_CONFIG({ orgCanonical: nextCanonical })

      await rootStore.UPDATE_USER_SESSION(token)
      customersStore.SET_SCOPE_DEPTH(0)
      customersStore.REMOVE_CUSTOMER_SCOPES()
      this.RESET_ORG_STATE('errors')
      this.RESET_ORG_STATE('available')
      useOidcMappingsStore().RESET_OIDC_MAPPINGS_STATE()
      await this.GET_ORGANIZATION(nextCanonical)
      // GET_ORGANIZATION fails silently (detail stays on the previous org); re-point nav there so it
      // can't outrun a failed switch (ENG-403).
      if (this.orgCanonical !== nextCanonical) useNavConfigStore().FETCH_NAV_CONFIG({ orgCanonical: this.orgCanonical })
      const { orgName } = this
      customersStore.SET_CURRENT_SCOPE({ name: orgName, canonical: nextCanonical, jwt: token })
      return true
    },
    SET_ORGANIZATION (org = {}) {
      const { blocked, ...organization } = org
      if (_.isEmpty(organization)) return
      this.detail = organization
      sessionStorage.setItem(LSK.ORGANIZATION, JSON.stringify(organization))
      localStorage.setItem(LSK.ORGANIZATION, JSON.stringify(organization))
    },
    async DELETE_ORGANIZATION ({ reqPage, $router, organization }) {
      const { canonical: orgCanonical, name: orgName } = organization

      const rootStore = useRootStore()

      const nextCanonical = rootStore.organizations.find((o) => o.canonical !== orgCanonical)?.canonical
      if (nextCanonical) this.SWITCH_TO_OTHER_ORG_AFTER_DELETE(nextCanonical)

      const { errors = null } = (await this.$cycloid.ydAPI.deleteOrg(orgCanonical)) || {}
      if (errors) {
        const alertStore = useAlertsStore()
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
        return
      }

      await rootStore.FETCH_ORGS({ reqPage })
      if (!_.isEmpty(rootStore.organizations)) await this.CHANGE_ORGANIZATION({ nextCanonical: nextCanonical || rootStore.organizations[0].canonical })
      else {
        this.RESET_ORGANIZATION()
        const licenceStore = useLicenceStore()
        const customersStore = useCustomersStore()
        customersStore.RESET_CUSTOMERS_STATE()
        licenceStore.RESET_LICENCE_STATE()
        const { data } = await this.$cycloid.ydAPI.refreshToken(null)
        await rootStore.UPDATE_USER_SESSION(data.token)
      }
      $router.push({ name: 'organizations' })
      const alertStore = useAlertsStore()
      alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.organization.deleted', { orgName }) })
    },
    async UPDATE_ORGANIZATION ({ organization }) {
      this.CLEAR_ERRORS('organization')

      // Send only what differs from the organization as loaded: the API keeps the fields left out, so a
      // concurrent change of another setting is not reverted with this page's stale copy
      const changes = _.omitBy(organization, (value, key) => _.isEqual(value, this.organization?.[key]))
      const body = { canonical: organization.canonical, name: organization.name, ...changes }
      const { data, errors = null } = (await this.$cycloid.ydAPI.updateOrg(body)) || {}
      if (data) {
        const { name: orgName } = data
        this.SET_ORGANIZATION(data)
        this.available.versionsByStackRef = []
        const alertStore = useAlertsStore()
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.organization.updated', { orgName }) })
      }
      if (errors) this.SET_ERRORS({ key: 'organization', errors })
    },
    async GET_ORGANIZATION_NAMING_RULES () {
      const { data, errors } = (await this.$cycloid.ydAPI.getOrganizationNamingRules(this.orgCanonical)) || {}
      if (!errors) {
        this.namingRules = { ...EMPTY_ORG_NAMING_RULES, ...data }
      } else {
        this.namingRules = { ...EMPTY_ORG_NAMING_RULES }
      }
      return { data, errors }
    },
    async UPDATE_ORGANIZATION_NAMING_RULES (payload) {
      const alertStore = useAlertsStore()
      const { data, errors } = (await this.$cycloid.ydAPI.updateOrganizationNamingRules(this.orgCanonical, payload)) || {}
      if (!errors) {
        this.namingRules = { ...EMPTY_ORG_NAMING_RULES, ...data || payload }
        this.namingRulesUpdatedAt = Date.now()
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.organization.namingRules.updated') })
      } else {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
      }
      return { data, errors }
    },
    async DELETE_ORGANIZATION_NAMING_RULES () {
      const alertStore = useAlertsStore()
      const { errors } = (await this.$cycloid.ydAPI.deleteOrganizationNamingRules(this.orgCanonical)) || {}
      if (!errors) {
        this.namingRules = { ...EMPTY_ORG_NAMING_RULES }
        this.namingRulesUpdatedAt = Date.now()
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.organization.namingRules.deleted') })
      } else {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
      }
      return { errors }
    },
    RESET_ORGANIZATION (orgsList = []) {
      const organization = orgsList[0] || {}
      if (_.isEmpty(organization)) {
        sessionStorage.removeItem(LSK.ORGANIZATION)
        localStorage.removeItem(LSK.ORGANIZATION)
        this.detail = null
      } else {
        this.detail = organization
        sessionStorage.setItem(LSK.ORGANIZATION, JSON.stringify(organization))
        localStorage.setItem(LSK.ORGANIZATION, JSON.stringify(organization))
      }
    },
    // Additional
    START_FETCH: makeStartFetchAction(),
    STOP_FETCH: makeStopFetchAction(),
    async GET_KPIS ({ favorite = false }) {
      this.CLEAR_ERRORS('KPIs')
      this.START_FETCH('KPIs')

      const { filters, orgCanonical } = this

      const beginMilliseconds = $date.format(new Date(filters.KPIs.datePeriod.value.begin), 'T')
      const endMilliseconds = $date.format(new Date(filters.KPIs.datePeriod.value.end), 'T')

      const { data, errors } = (await this.$cycloid.ydAPI.getKPIs(
        orgCanonical,
        filters.KPIs?.project,
        beginMilliseconds,
        endMilliseconds,
        favorite,
      )) || {}
      if (data) this.SET_KPIS(data)
      if (errors) this.SET_ERRORS({ key: 'KPIs', errors })
      this.STOP_FETCH('KPIs')
    },
    SET_KPIS (KPIs) {
      this.KPIs = KPIs
    },
    SET_FILTERS ({ key, value }) {
      this.filters[key] = value
    },
    SET_SUMMARY (summary) {
      for (const [key, value] of _.entries(summary)) {
        this.summary[getKey(key)] = value || 0
      }
    },
    RESET_SUMMARY () {
      this.summary = state().summary
    },
    async GET_ORG_SUMMARY () {
      this.START_FETCH('summary')
      this.RESET_SUMMARY()
      const { data } = (await this.$cycloid.ydAPI.getOrgSummary(this.orgCanonical)) || {}
      if (data) this.SET_SUMMARY(data)
      this.STOP_FETCH('summary')
    },
    /** @param {{ invitedMembers: Array, $router }} payload */
    async INVITE_ORG_MEMBERS ({ invitedMembers, $router }) {
      const alertStore = useAlertsStore()
      const { orgCanonical, orgName } = this

      this.CLEAR_ERRORS('invitedMembers')
      const allErrors = []
      let successfullyInvitedEmailCount = 0

      const promises = invitedMembers.map(async (invitedMember) => {
        const { errors } = (await this.$cycloid.ydAPI.inviteUserToOrgMember(orgCanonical, invitedMember)) || {}
        if (errors) allErrors.push(...errors.map((error) => ({ ...error, details: [...(error.details || []), invitedMember.email] })))
        else successfullyInvitedEmailCount++
      })
      await Promise.allSettled(promises)

      const successMessage = i18n.global.t('alerts.success.organization.members.invited', successfullyInvitedEmailCount, { named: { orgName, nb: successfullyInvitedEmailCount } })
      if (successfullyInvitedEmailCount > 0) alertStore.SHOW_ALERT({ type: 'success', content: successMessage })
      if (!_.isEmpty(allErrors)) this.SET_ERRORS({ key: 'invitedMembers', errors: allErrors })
      else if ($router) $router.push({ name: 'members' })
    },
    async RESEND_INVITES ({ invites }) {
      const alertStore = useAlertsStore()
      this.CLEAR_ERRORS('invites')
      const allErrors = []
      let successfullyResentCount = 0
      const { orgCanonical, orgName } = this

      const promises = invites.map(async (invite) => {
        const { errors } = (await this.$cycloid.ydAPI.resendInvitation(orgCanonical, invite.id)) || {}
        if (errors) allErrors.push(...errors.map((error) => ({ ...error, details: [...(error.details || []), invite.email] })))
        else successfullyResentCount++
      })
      await Promise.allSettled(promises)

      const successMessage = i18n.global.t('alerts.success.organization.members.invitesResent', successfullyResentCount, { named: { orgName, nb: successfullyResentCount } })
      if (successfullyResentCount > 0) alertStore.SHOW_ALERT({ type: 'success', content: successMessage })
      if (!_.isEmpty(allErrors)) {
        alertStore.SHOW_ALERT({ type: 'error', content: allErrors })
        this.SET_ERRORS({ key: 'invites', errors: allErrors })
      }
    },

    async UNASSIGN_ORG_MEMBERS ({ ids, $router }) {
      const alertStore = useAlertsStore()
      this.CLEAR_ERRORS()
      const allErrors = []
      const successfullyRemovedIds = []
      const { orgCanonical, orgName } = this

      const promises = ids.map(async (id) => {
        const { errors } = (await this.$cycloid.ydAPI.removeOrgMember(orgCanonical, id)) || {}
        if (errors) allErrors.push(...errors)
        else successfullyRemovedIds.push(id)
      })
      await Promise.allSettled(promises)

      const idsRemoved = _.$getListFromArray(successfullyRemovedIds, { boldItems: true })
      const successMessage = i18n.global.t('alerts.success.organization.members.unassigned', successfullyRemovedIds.length, { named: { orgName, userNames: idsRemoved } })
      if (!_.isEmpty(successfullyRemovedIds)) alertStore.SHOW_ALERT({ type: 'success', content: successMessage })
      if (!_.isEmpty(allErrors)) this.SET_ERRORS({ errors: allErrors })
      else if ($router) $router.push({ name: 'members' })
    },
    /**
     * Fetches stack versions for multiple stack refs
     * @param {{ stackRefs: Array<string> }} payload - Array of stack refs to fetch versions for
     */
    async FETCH_VERSIONS_BY_STACK ({ stackRefs }) {
      if (!Array.isArray(stackRefs) || stackRefs.length === 0) return

      const refsToFetch = stackRefs.filter((ref) => !this.available.versionsByStackRef[ref])

      if (!refsToFetch.length) return

      const promises = refsToFetch.map(async (stackRef) => {
        const { data } = await this.$cycloid.ydAPI.getServiceCatalogVersions(this.orgCanonical, stackRef) || {}
        if (data) {
          _.set(this.available.versionsByStackRef, stackRef, data)
        }
      })

      await Promise.all(promises)
    },
    async CREATE_ENVIRONMENT_TYPE ({ environmentType }) {
      const alertStore = useAlertsStore()

      this.CLEAR_ERRORS('environmentTypes')
      const { data, errors } = await this.$cycloid.ydAPI.createEnvironmentType(this.orgCanonical, environmentType) || {}

      if (errors) {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_ERRORS({ key: 'environmentTypes', errors })
      } else {
        const content = i18n.global.t('alerts.success.environmentType.created')
        alertStore.SHOW_ALERT({ type: 'success', content })
      }
      return _.isEmpty(errors) ? (data ?? true) : false
    },
    async UPDATE_ENVIRONMENT_TYPE ({ canonical, environmentType }) {
      const alertStore = useAlertsStore()

      this.CLEAR_ERRORS('environmentTypes')
      const { errors } = await this.$cycloid.ydAPI.updateEnvironmentType(this.orgCanonical, canonical, environmentType) || {}

      if (errors) {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_ERRORS({ key: 'environmentTypes', errors })
      } else {
        const content = i18n.global.t('alerts.success.environmentType.updated')
        alertStore.SHOW_ALERT({ type: 'success', content })
      }
      return _.isEmpty(errors)
    },
    async DELETE_ENVIRONMENT_TYPE ({ canonical }) {
      const alertStore = useAlertsStore()

      this.CLEAR_ERRORS('environmentTypes')
      const { errors } = await this.$cycloid.ydAPI.deleteEnvironmentType(this.orgCanonical, canonical) || {}

      if (errors) {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_ERRORS({ key: 'environmentTypes', errors })
      } else {
        const content = i18n.global.t('alerts.success.environmentType.deleted')
        alertStore.SHOW_ALERT({ type: 'success', content })
      }
      return _.isEmpty(errors)
    },
    async SET_ENVIRONMENT_TYPE_LABEL_SELECTOR ({ canonical, labelSelector }) {
      const alertStore = useAlertsStore()

      this.CLEAR_ERRORS('environmentTypes')
      const { errors } = await this.$cycloid.ydAPI.setEnvironmentTypeLabelSelector(this.orgCanonical, canonical, labelSelector) || {}

      if (errors) {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_ERRORS({ key: 'environmentTypes', errors })
      }
      return _.isEmpty(errors)
    },
    async DELETE_ENVIRONMENT_TYPE_LABEL_SELECTOR ({ canonical }) {
      const alertStore = useAlertsStore()

      this.CLEAR_ERRORS('environmentTypes')
      const { errors } = await this.$cycloid.ydAPI.deleteEnvironmentTypeLabelSelector(this.orgCanonical, canonical) || {}

      if (errors) {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_ERRORS({ key: 'environmentTypes', errors })
      }
      return _.isEmpty(errors)
    },
    async CREATE_ORGANIZATION_ENVIRONMENT ({ environment }) {
      const alertStore = useAlertsStore()

      this.CLEAR_ERRORS('environments')
      const response = await this.$cycloid.ydAPI.createOrganizationEnvironment(this.orgCanonical, environment)
      const { data, errors } = response || {}
      if (errors || !response) {
        if (errors) {
          alertStore.SHOW_ALERT({ type: 'error', content: errors })
          this.SET_ERRORS({ key: 'environments', errors })
        }
        return { success: false, data: null }
      }
      const content = i18n.global.t('alerts.success.environment.created')
      alertStore.SHOW_ALERT({ type: 'success', content })
      return { success: true, data }
    },
  },
})
